'use client';

import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {PauseIcon, PlayIcon} from '@heroicons/react/20/solid';
import VimeoPlayer from '@vimeo/player';
import clsx from 'clsx';

import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';

import type {MediaFragment} from '../../schema/primitives/media';

interface Props {
	video?: MediaFragment['video'];
	disabledControls?: boolean;
}

export const MediaVideoOriginal: FC<Props> = ({video, disabledControls}) => {
	const [{height, width}, setDimensions] = useState({
		height: 0,
		width: 0,
	});
	const [playing, setPlaying] = useState(false);
	const [player, setPlayer] = useState<VimeoPlayer | null>(null);
	const [hiddenPause, setHiddenPause] = useState(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const autoplay = video?.autoplay;
	const hasReducedMotionPreference = usePrefersReducedMotion();
	const videoParams = new URLSearchParams({
		autoplay:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '1'
				: '0',
		loop:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '1'
				: '0',
		autopause:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '0'
				: '1',
		title: '0',
		byline: '0',
		portrait: '0',
		controls: '0',
		muted: '1',
		fullscreen: '1',
	});

	// Initialize Vimeo player
	useEffect(() => {
		if (!iframeRef.current) {
			return;
		}
		const player = new VimeoPlayer(iframeRef.current);
		setPlayer(player);
	}, [iframeRef]);

	// Play/pause video when clicking on the play button
	const onControlClick = useCallback(() => {
		if (playing) {
			player?.pause();
			setPlaying(false);
		} else {
			player?.play();
			setPlaying(true);
		}
	}, [player, playing]);

	// The pause icon should be hidden when the video is playing
	useEffect(() => {
		if (!playing) {
			return;
		}
		const timeout = setTimeout(() => {
			setHiddenPause(true);
		}, 1000);
		return () => {
			clearTimeout(timeout);
			setHiddenPause(false);
		};
	}, [playing]);

	// Fetch video dimensions from Vimeo API
	useEffect(() => {
		if (!video?.videoId) {
			return;
		}

		const getVideoDimensions = async () => {
			try {
				const response = await fetch(
					`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video.videoId}`,
				);
				if (!response.ok) {
					throw new Error('Could not fetch video dimensions');
				}

				const dimensions = await response.json();
				setDimensions(dimensions);
			} catch (error) {
				console.error(error);
				setDimensions({height: 0, width: 0});
			}
		};

		getVideoDimensions();
	}, [video?.videoId]);

	return (
		<div
			className={clsx(
				'relative',
				'max-w-full',
				'max-h-full',
				'flex',
				'justify-center',
				'items-center',
				'overflow-hidden',
			)}
			style={{aspectRatio: `${width}/${height}`}}
		>
			{video?.videoId && (
				<iframe
					name="vimeo-player"
					title="vimeo-player"
					src={`https://player.vimeo.com/video/${
						video.videoId
					}?${videoParams.toString()}`}
					allow="autoplay; muted;"
					className={clsx('absolute', 'pointer-events-none')}
					style={{width: '100%', height: '100%'}}
					ref={iframeRef}
					tabIndex={disabledControls ? -1 : undefined}
				></iframe>
			)}

			{!autoplay && !disabledControls && (
				<button
					className={clsx(
						'absolute',
						'h-full',
						'w-full',
						'flex',
						'items-center',
						'justify-center',
						'group',
					)}
					onClick={onControlClick}
				>
					{playing ? (
						<PauseIcon
							className={clsx(
								'absolute',
								'h-14',
								'w-14',
								'text-turquoise-700',
								hiddenPause ? 'opacity-0' : 'opacity-100',
								'transition-all',
								'duration-300',
								'p-2',
								'rounded-full',
								'group-focus-visible:ring-2',
								'group-focus-visible:ring-turquoise-700',
							)}
						/>
					) : (
						<PlayIcon
							className={clsx(
								'absolute',
								'h-14',
								'w-14',
								'text-turquoise-700',
								'p-2',
								'rounded-full',
								'group-focus-visible:ring-2',
								'group-focus-visible:ring-turquoise-700',
							)}
						/>
					)}
				</button>
			)}
		</div>
	);
};
